import React, { useState } from 'react';
import { format, parseISO } from 'date-fns';
import { Battery, Zap, Clock, Info, X, AlertTriangle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useQuery } from '@tanstack/react-query';
import { BatteryControlSchedule, BatteryRecommendation } from '../types/api';
import { getManualOverrides, createManualOverride, updateManualOverride, deleteManualOverride } from '../services/api';
import apiService from '../services/api';

interface BatteryControlSchedulesTileProps {
  schedules: BatteryControlSchedule[];
  recommendations?: BatteryRecommendation[];
  lastUpdated: string | null;
  dataId?: number | null;
  isLoading?: boolean;
  onSchedulesChange?: () => void;
}

type OverrideType = 'charge' | 'discharge' | 'override';

interface OverrideDialogProps {
  isOpen: boolean;
  onClose: () => void;
  type: OverrideType;
  schedule?: BatteryControlSchedule;
  onSave: () => void;
  inverterControlEnabled?: boolean;
}

const OverrideDialog: React.FC<OverrideDialogProps> = ({ isOpen, onClose, type, schedule, onSave, inverterControlEnabled = true }) => {
  const getInitialDateTime = () => {
    if (schedule) {
      const date = parseISO(schedule.start);
      return {
        date: format(date, "yyyy-MM-dd"),
        time: format(date, "HH:mm")
      };
    }
    const now = new Date();
    return {
      date: format(now, "yyyy-MM-dd"),
      time: format(now, "HH:mm")
    };
  };

  const [dateTime, setDateTime] = useState(getInitialDateTime());
  
  const updateStartTime = (date: string, time: string) => {
    setDateTime({ date, time });
  };
  
  const getStartTimeISO = () => {
    return `${dateTime.date}T${dateTime.time}`;
  };
  const [duration, setDuration] = useState<number>(() => {
    if (schedule) {
      const calculatedDuration = Math.round((parseISO(schedule.end).getTime() - parseISO(schedule.start).getTime()) / (1000 * 60 * 60) * 2) / 2;
      // Clamp to 0-6 hours range
      return Math.max(0, Math.min(6, calculatedDuration));
    }
    return 1;
  });
  const [targetLevel, setTargetLevel] = useState<number>(schedule?.target_level || (type === 'charge' ? 100 : 10));
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const now = new Date();
  const allowedPastMinutes = schedule ? 0 : 15;
  const earliestAllowed = new Date(now.getTime() - allowedPastMinutes * 60 * 1000);
  const minDate = format(earliestAllowed, "yyyy-MM-dd");
  const minTime = format(earliestAllowed, "HH:mm");

  // Update duration and dateTime when schedule changes (e.g., after an update)
  React.useEffect(() => {
    if (schedule && isOpen) {
      // Recalculate duration from schedule
      const calculatedDuration = Math.round((parseISO(schedule.end).getTime() - parseISO(schedule.start).getTime()) / (1000 * 60 * 60) * 2) / 2;
      setDuration(Math.max(0, Math.min(6, calculatedDuration)));
      
      // Update dateTime from schedule
      const date = parseISO(schedule.start);
      setDateTime({
        date: format(date, "yyyy-MM-dd"),
        time: format(date, "HH:mm")
      });
      
      // Update target level
      setTargetLevel(schedule.target_level || (type === 'charge' ? 100 : 10));
    } else if (!schedule && isOpen) {
      // Reset to defaults for new override
      const now = new Date();
      setDateTime({
        date: format(now, "yyyy-MM-dd"),
        time: format(now, "HH:mm")
      });
      setDuration(1);
      setTargetLevel(type === 'charge' ? 100 : 10);
    }
  }, [schedule, isOpen, type]);

  if (!isOpen) return null;

  const handleSave = async () => {
    if (!inverterControlEnabled) {
      toast.error('Inverter control is disabled. Cannot save overrides when inverter control is off.');
      return;
    }
    
    setIsSaving(true);
    try {
      // Parse the date/time string as local time
      // parseISO without timezone info defaults to UTC, so we need to construct a proper Date object
      const [datePart, timePart] = getStartTimeISO().split('T');
      const [year, month, day] = datePart.split('-').map(Number);
      const [hours, minutes] = timePart.split(':').map(Number);
      
      // Create a Date object in local timezone
      const startLocal = new Date(year, month - 1, day, hours, minutes, 0, 0);
      if (startLocal < earliestAllowed) {
        toast.error(schedule ? 'Start time must be in the future.' : 'Start time can be up to 15 minutes in the past.');
        return;
      }
      
      // Add duration (in hours, can be decimal like 1.5)
      const endLocal = new Date(startLocal.getTime() + (duration * 60 * 60 * 1000));
      
      // Convert to UTC for storage (backend will convert back to local when sending to inverter)
      // This preserves the local time the user entered
      const overrideData = {
        start: startLocal.toISOString(),
        end: endLocal.toISOString(),
        action: (type === 'override' ? 'hold' : type) as 'charge' | 'discharge' | 'hold',
        target_level: type === 'override' ? undefined : targetLevel,
      };

      if (schedule) {
        await updateManualOverride(schedule.id, overrideData);
      } else {
        await createManualOverride(overrideData);
      }
      onSave();
      onClose();
      toast.success(schedule ? 'Override updated successfully' : 'Override created successfully');
    } catch (error: any) {
      console.error('Error saving override:', error);
      const errorMsg = error?.response?.data?.detail || error?.message || 'Failed to save override. Please try again.';
      toast.error(errorMsg);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!schedule) return;
    
    if (!inverterControlEnabled) {
      toast.error('Inverter control is disabled. Cannot delete overrides when inverter control is off.');
      setShowDeleteConfirm(false);
      return;
    }
    
    setIsSaving(true);
    setShowDeleteConfirm(false);
    try {
      await deleteManualOverride(schedule.id);
      onSave();
      onClose();
      toast.success('Override deleted successfully');
    } catch (error: any) {
      console.error('Error deleting override:', error);
      const errorMsg = error?.response?.data?.detail || error?.message || 'Failed to delete override. Please try again.';
      toast.error(errorMsg);
    } finally {
      setIsSaving(false);
    }
  };

  const getDialogIcon = () => {
    switch (type) {
      case 'charge':
        return <Zap className="h-5 w-5 text-red-600" />;
      case 'discharge':
        return <Battery className="h-5 w-5 text-yellow-600" />;
      case 'override':
        return (
          <img 
            src={`${process.env.PUBLIC_URL || ''}/stop-icon.png`}
            alt="Stop" 
            className="h-5 w-5 object-contain"
          />
        );
      default:
        return <Info className="h-5 w-5 text-gray-600" />;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            {getDialogIcon()}
            <h3 className="text-lg font-semibold text-gray-900">
              {schedule ? 'Edit' : 'Create'} {type === 'override' ? 'Hold' : `${type.charAt(0).toUpperCase() + type.slice(1)} Override`}
            </h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Start Date & Time
            </label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Date</label>
                <input
                  type="date"
                  value={dateTime.date}
                  onChange={(e) => updateStartTime(e.target.value, dateTime.time)}
                  min={minDate}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Time</label>
                <input
                  type="time"
                  value={dateTime.time}
                  onChange={(e) => updateStartTime(dateTime.date, e.target.value)}
                  min={dateTime.date === minDate ? minTime : undefined}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Duration: {duration} {duration === 1 ? 'hour' : 'hours'}
            </label>
            <input
              type="range"
              min="0"
              max="6"
              step="0.5"
              value={duration}
              onChange={(e) => setDuration(parseFloat(e.target.value) || 0)}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>0h</span>
              <span>3h</span>
              <span>6h</span>
            </div>
          </div>

          {type !== 'override' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {type === 'charge' ? 'Maximum' : 'Minimum'} Battery Level: {targetLevel}%
              </label>
              <input
                type="range"
                min="10"
                max="100"
                step="1"
                value={targetLevel}
                onChange={(e) => setTargetLevel(parseInt(e.target.value))}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>10%</span>
                <span>100%</span>
              </div>
            </div>
          )}

          <div className="flex space-x-3 pt-4">
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
            {schedule && (
              <button
                onClick={handleDeleteClick}
                disabled={isSaving}
                className="flex-1 bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Delete
              </button>
            )}
            <button
              onClick={onClose}
              disabled={isSaving}
              className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center space-x-3 mb-4">
              <AlertTriangle className="h-6 w-6 text-red-600" />
              <h3 className="text-lg font-semibold text-gray-900">
                Delete Override
              </h3>
            </div>
            
            <p className="text-gray-700 mb-6">
              Are you sure you want to delete this override? This action cannot be undone.
            </p>

            <div className="flex space-x-3">
              <button
                onClick={handleDeleteConfirm}
                disabled={isSaving}
                className="flex-1 bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? 'Deleting...' : 'Delete'}
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isSaving}
                className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ScheduleItem: React.FC<{ schedule: BatteryControlSchedule; index: number; getActionIcon: (action: string) => React.ReactElement; getActionColor: (action: string) => string; formatTime: (timeStr: string) => string; formatDate: (timeStr: string) => string; onClick: (schedule: BatteryControlSchedule) => void; inverterControlEnabled?: boolean }> = ({ schedule, index, getActionIcon, getActionColor, formatTime, formatDate, onClick, inverterControlEnabled = true }) => {
  const isManual = schedule.source === 'manual';
  const canEdit = isManual && inverterControlEnabled;
  
  return (
    <div
      key={schedule.id || `schedule-${index}`}
      className={`p-3 rounded-lg border min-h-[80px] flex flex-col ${getActionColor(schedule.action)} ${
        canEdit ? 'cursor-pointer hover:shadow-md transition-shadow' : isManual ? 'opacity-60' : ''
      }`}
      onClick={() => onClick(schedule)}
    >
      <div className="flex items-start justify-between flex-shrink-0">
        <div className="flex flex-col">
          <div className="flex items-center space-x-2">
            {getActionIcon(schedule.action)}
            <span className="font-medium text-sm capitalize">{schedule.action}</span>
          </div>
        </div>
        <div className="text-right text-xs flex-shrink-0">
          <div className="font-medium leading-tight">
            {formatTime(schedule.start)} - {formatTime(schedule.end)}
          </div>
          <div className="text-xs leading-tight" style={{ opacity: 0.75 }}>
            {formatDate(schedule.start)}
          </div>
        </div>
      </div>
      
      <div className="mt-2 flex-1">
        {/* Show Min/Max for manual overrides, or for any schedule that has target_level */}
        {(schedule.source === 'manual' && (schedule.action === 'charge' || schedule.action === 'discharge')) || schedule.target_level ? (
          <div className="text-sm">
            {schedule.action === 'charge' ? 'Max' : 'Min'}: {schedule.target_level || (schedule.action === 'charge' ? 100 : 10)}%
          </div>
        ) : null}
        
        {schedule.reason && (
            <div className="text-xs mt-1" style={{ opacity: 0.75 }}>
              {schedule.reason}
            </div>
        )}
        
        {schedule.average_price !== undefined && (
          <div className="text-sm font-medium">
            Average Price: ${(schedule.average_price / 100).toFixed(2)}/kWh
          </div>
        )}
        
        {schedule.total_price !== undefined && (
          <div className="text-sm font-medium mt-1">
            {schedule.action === 'charge' ? 'Projected Cost' : 'Projected Income'}: ${(schedule.total_price / 100).toFixed(2)}
          </div>
        )}
      </div>
    </div>
  );
};

const RecommendationItem: React.FC<{ recommendation: BatteryRecommendation; index: number; getActionIcon: (action: string) => React.ReactElement; getActionColor: (action: string) => string; formatTime: (timeStr: string) => string; formatDate: (timeStr: string) => string }> = ({ recommendation, index, getActionIcon, getActionColor, formatTime, formatDate }) => {
  return (
    <div
      key={`recommendation-${index}`}
      className={`p-3 rounded-lg border min-h-[80px] flex flex-col ${getActionColor(recommendation.action)}`}
    >
      <div className="flex items-start justify-between flex-shrink-0">
        <div className="flex flex-col">
          <div className="flex items-center space-x-2">
            {getActionIcon(recommendation.action)}
            <span className="font-medium text-sm capitalize">{recommendation.action}</span>
          </div>
        </div>
        <div className="text-right text-xs flex-shrink-0">
          <div className="font-medium leading-tight">
            {formatTime(recommendation.start)} - {formatTime(recommendation.end)}
          </div>
          <div className="text-xs leading-tight" style={{ opacity: 0.75 }}>
            {formatDate(recommendation.start)}
          </div>
        </div>
      </div>
      
      <div className="mt-2 flex-1">
        {recommendation.average_price !== undefined && (
          <div className="text-sm font-medium">
            Average Price: ${(recommendation.average_price / 100).toFixed(2)}/kWh
          </div>
        )}
        
        {recommendation.total_price !== undefined && (
          <div className="text-sm font-medium mt-1">
            {recommendation.action === 'charge' ? 'Projected Cost' : 'Projected Income'}: ${(recommendation.total_price / 100).toFixed(2)}
          </div>
        )}
      </div>
    </div>
  );
};

const BatteryControlSchedulesTile: React.FC<BatteryControlSchedulesTileProps> = ({
  schedules,
  recommendations = [],
  lastUpdated,
  dataId = null,
  isLoading = false,
  onSchedulesChange
}) => {
  const [activeTab, setActiveTab] = useState<'scheduled' | 'recommended' | 'manual'>('scheduled');
  const [dialogOpen, setDialogOpen] = useState<{ type: OverrideType; schedule?: BatteryControlSchedule } | null>(null);
  const [manualOverrides, setManualOverrides] = useState<BatteryControlSchedule[]>([]);
  const [manualOverridesLoading, setManualOverridesLoading] = useState(false);
  
  // Get inverter control setting
  const { data: settings } = useQuery({
    queryKey: ['settings'],
    queryFn: () => apiService.getSettings(),
  });
  const inverterControlEnabled = settings?.settings?.INVERTER_CONTROL_ENABLED ?? true;

  // Fetch manual overrides when component mounts or when tab changes to manual
  React.useEffect(() => {
    const fetchManualOverrides = async () => {
      if (activeTab === 'manual') {
        setManualOverridesLoading(true);
        try {
          const response = await getManualOverrides(dataId ?? undefined);
          setManualOverrides(response.overrides || []);
        } catch (error) {
          console.error('Error fetching manual overrides:', error);
          setManualOverrides([]);
        } finally {
          setManualOverridesLoading(false);
        }
      }
    };
    fetchManualOverrides();
  }, [activeTab, onSchedulesChange, dataId]);

  // Listen for manual override updates (refresh Manual tab only)
  React.useEffect(() => {
    const handleManualOverrideUpdate = () => {
      console.log('📝 Manual override updated, refreshing Manual tab...');
      // Always refresh manual overrides when this event fires
      getManualOverrides(dataId ?? undefined).then(response => {
        setManualOverrides(response.overrides || []);
      }).catch(error => {
        console.error('Error refreshing manual overrides:', error);
      });
      // Also trigger schedules refresh since override changes affect schedules
      if (onSchedulesChange) {
        onSchedulesChange();
      }
    };

    window.addEventListener('manualOverrideUpdated', handleManualOverrideUpdate);
    
    return () => {
      window.removeEventListener('manualOverrideUpdated', handleManualOverrideUpdate);
    };
  }, [onSchedulesChange, dataId]);
  const getActionIcon = (action: string) => {
    switch (action) {
      case 'charge':
        return <Zap className="h-4 w-4 text-red-600" />;
      case 'discharge':
        return <Battery className="h-4 w-4 text-yellow-600" />;
      case 'hold':
        return <Clock className="h-4 w-4 text-blue-600" />;
      default:
        return <Info className="h-4 w-4 text-gray-600" />;
    }
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case 'charge':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'discharge':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'hold':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatTime = (timeStr: string) => {
    try {
      return format(parseISO(timeStr), 'HH:mm');
    } catch {
      return timeStr;
    }
  };

  const formatDate = (timeStr: string) => {
    try {
      return format(parseISO(timeStr), 'MMM dd');
    } catch {
      return timeStr;
    }
  };



  if (isLoading) {
    return (
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Battery Control Schedules</h2>
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  // Safety check: ensure schedules is an array
  if (!Array.isArray(schedules)) {
    return (
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Battery Control Schedules</h2>
        <div className="text-center py-8 text-gray-500">
          <Battery className="h-12 w-12 mx-auto mb-2 text-gray-300" />
          <p>No battery control schedules available</p>
        </div>
      </div>
    );
  }

  const handleScheduleClick = (schedule: BatteryControlSchedule) => {
    if (schedule.source === 'manual' && inverterControlEnabled) {
      const overrideType = schedule.action === 'hold' ? 'override' : schedule.action as OverrideType;
      setDialogOpen({ type: overrideType, schedule });
    } else if (schedule.source === 'manual' && !inverterControlEnabled) {
      toast.error('Inverter control is disabled. Cannot edit overrides when inverter control is off.');
    }
  };

  // Separate schedules into actual schedules (from inverter)
  // Scheduled tab shows only the schedules returned from get_battery_control_schedules (inverter)
  // These are schedules that don't have source='manual'
  const actualSchedules = schedules.filter(s => s.source !== 'manual');

  // Sort schedules by start time
  const sortedActualSchedules = [...actualSchedules].sort((a, b) => {
    try {
      const dateA = parseISO(a.start).getTime();
      const dateB = parseISO(b.start).getTime();
      return dateA - dateB;
    } catch {
      return 0;
    }
  });

  // Filter out expired manual overrides and sort
  const referenceTime = lastUpdated ? parseISO(lastUpdated) : new Date();
  const filteredManualOverrides = manualOverrides.filter((schedule) => {
    try {
      const endTime = parseISO(schedule.end);
      return endTime > referenceTime;
    } catch {
      // If parsing fails, keep the schedule
      return true;
    }
  });

  const sortedManualOverrides = [...filteredManualOverrides].sort((a, b) => {
    try {
      const dateA = parseISO(a.start).getTime();
      const dateB = parseISO(b.start).getTime();
      return dateA - dateB;
    } catch {
      return 0;
    }
  });

  // Sort recommendations by start time
  const sortedRecommendations = [...(recommendations || [])].sort((a, b) => {
    try {
      const dateA = parseISO(a.start).getTime();
      const dateB = parseISO(b.start).getTime();
      return dateA - dateB;
    } catch {
      return 0;
    }
  });

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <h2 className="text-lg font-semibold text-gray-900">Battery Control Schedules</h2>
        </div>
      </div>

      {/* Override Buttons - Greyed out if inverter control is off */}
      <div className="flex space-x-2 mb-4">
        <button
          onClick={() => {
            if (inverterControlEnabled) {
              setActiveTab('manual');
              setDialogOpen({ type: 'charge' });
            } else {
              toast.error('Inverter control is disabled. Cannot create overrides when inverter control is off.');
            }
          }}
          className={`flex-1 text-white px-2 py-2.5 rounded-md text-sm font-medium h-10 flex items-center justify-center ${
            inverterControlEnabled 
              ? 'bg-red-600 hover:bg-red-700' 
              : 'bg-gray-400 cursor-not-allowed'
          }`}
          disabled={!inverterControlEnabled}
        >
          Charge
        </button>
        <button
          onClick={() => {
            if (inverterControlEnabled) {
              setActiveTab('manual');
              setDialogOpen({ type: 'discharge' });
            } else {
              toast.error('Inverter control is disabled. Cannot create overrides when inverter control is off.');
            }
          }}
          className={`flex-1 text-white px-4 py-2.5 rounded-md text-sm font-medium h-10 flex items-center justify-center ${
            inverterControlEnabled 
              ? 'bg-yellow-600 hover:bg-yellow-700' 
              : 'bg-gray-400 cursor-not-allowed'
          }`}
          disabled={!inverterControlEnabled}
        >
          Discharge
        </button>
        <button
          onClick={() => {
            if (inverterControlEnabled) {
              setActiveTab('manual');
              setDialogOpen({ type: 'override' });
            } else {
              toast.error('Inverter control is disabled. Cannot create overrides when inverter control is off.');
            }
          }}
          className={`flex-1 text-white px-4 py-2.5 rounded-md text-sm font-medium h-10 flex items-center justify-center ${
            inverterControlEnabled 
              ? 'bg-blue-600 hover:bg-blue-700' 
              : 'bg-gray-400 cursor-not-allowed'
          }`}
          disabled={!inverterControlEnabled}
        >
          Hold
        </button>
      </div>

      {/* Tabs */}
      <div className="flex space-x-1 mb-3 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('scheduled')}
          className={`px-2 py-1 text-xs font-medium transition-colors ${
            activeTab === 'scheduled'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Scheduled
        </button>
        <button
          onClick={() => setActiveTab('recommended')}
          className={`px-2 py-1 text-xs font-medium transition-colors ${
            activeTab === 'recommended'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Recommended
        </button>
        <button
          onClick={() => setActiveTab('manual')}
          className={`px-2 py-1 text-xs font-medium transition-colors ${
            activeTab === 'manual'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Manual
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'scheduled' && (
        <>
          {sortedActualSchedules.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Battery className="h-12 w-12 mx-auto mb-2 text-gray-300" />
              <p>No scheduled battery control available</p>
            </div>
          ) : (
            <div className="space-y-3">
              {sortedActualSchedules.map((schedule, index) => (
                <ScheduleItem
                  key={schedule.id || `schedule-${index}`}
                  schedule={schedule}
                  index={index}
                  getActionIcon={getActionIcon}
                  getActionColor={getActionColor}
                  formatTime={formatTime}
                  formatDate={formatDate}
                  onClick={handleScheduleClick}
                  inverterControlEnabled={inverterControlEnabled}
                />
              ))}
            </div>
          )}
        </>
      )}

      {activeTab === 'recommended' && (
        <>
          {sortedRecommendations.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Battery className="h-12 w-12 mx-auto mb-2 text-gray-300" />
              <p>No battery recommendations available</p>
            </div>
          ) : (
            <div className="space-y-3">
              {sortedRecommendations.map((recommendation, index) => (
                <RecommendationItem
                  key={`recommendation-${index}`}
                  recommendation={recommendation}
                  index={index}
                  getActionIcon={getActionIcon}
                  getActionColor={getActionColor}
                  formatTime={formatTime}
                  formatDate={formatDate}
                />
              ))}
            </div>
          )}
        </>
      )}

      {activeTab === 'manual' && (
        <>
          {manualOverridesLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
          ) : sortedManualOverrides.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Battery className="h-12 w-12 mx-auto mb-2 text-gray-300" />
              <p>No manual overrides available</p>
            </div>
          ) : (
            <div className="space-y-3">
              {sortedManualOverrides.map((schedule, index) => (
                <ScheduleItem
                  key={schedule.id || `schedule-${index}`}
                  schedule={schedule}
                  index={index}
                  getActionIcon={getActionIcon}
                  getActionColor={getActionColor}
                  formatTime={formatTime}
                  formatDate={formatDate}
                  onClick={handleScheduleClick}
                  inverterControlEnabled={inverterControlEnabled}
                />
              ))}
            </div>
          )}
        </>
      )}

      {dialogOpen && (
        <OverrideDialog
          isOpen={true}
          onClose={() => setDialogOpen(null)}
          type={dialogOpen.type}
          schedule={dialogOpen.schedule}
          onSave={async () => {
            // Refresh manual overrides when a change is made
            if (activeTab === 'manual') {
              try {
                const response = await getManualOverrides();
                setManualOverrides(response.overrides || []);
              } catch (error: any) {
                console.error('Error refreshing manual overrides:', error);
              }
            }
            if (onSchedulesChange) {
              onSchedulesChange();
            }
          }}
        />
      )}
    </div>
  );
};

export default BatteryControlSchedulesTile;
